const root = "/v1/browser/session";
const proof = /^[A-Za-z0-9_-]{43}$/;
const account = /^[0-9a-f-]{36}$/i;
export interface BrowserSession {
  authenticated: true;
  account_id: string;
  expires_at: string;
}
export interface BrowserGrant {
  exchange_code: string;
  account_id: string;
  expires_at: string;
}

export function browserSessionClient(transport: typeof fetch = fetch) {
  async function post(path: string, body: unknown, accessToken?: string) {
    const response = await transport(root + path, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000),
    });
    if (response.status === 401) return undefined;
    if (!response.ok) {
      if (response.status === 409) throw new Error("ACCOUNT_MISMATCH");
      throw new Error("BROWSER_SESSION_FAILED");
    }
    if (response.status === 204) return undefined;
    return response.json() as Promise<unknown>;
  }
  function session(value: unknown): BrowserSession {
    const data = value as Partial<BrowserSession> | undefined;
    if (
      data?.authenticated !== true ||
      !account.test(data.account_id ?? "") ||
      !Number.isFinite(Date.parse(data.expires_at ?? ""))
    )
      throw new Error("INVALID_BROWSER_SESSION");
    return data as BrowserSession;
  }
  return {
    async status(): Promise<BrowserSession | undefined> {
      const value = await post("/status", {});
      return value === undefined ? undefined : session(value);
    },
    async exchange(exchangeCode: string, receiverVerifier: string) {
      return session(
        await post("/exchange", {
          exchange_code: exchangeCode,
          receiver_verifier: receiverVerifier,
        }),
      );
    },
    async link(accessToken: string) {
      return session(await post("/link", {}, accessToken));
    },
    async handoff(receiverChallenge: string): Promise<BrowserGrant> {
      const value = (await post("/handoff", {
        receiver_challenge: receiverChallenge,
      })) as Partial<BrowserGrant> | undefined;
      if (
        !value ||
        !proof.test(value.exchange_code ?? "") ||
        !account.test(value.account_id ?? "") ||
        !Number.isFinite(Date.parse(value.expires_at ?? ""))
      )
        throw new Error("INVALID_BROWSER_GRANT");
      return value as BrowserGrant;
    },
    async logout() {
      await post("/logout", {});
    },
  };
}

// Cookie writes and logout are serialized across account tabs. A mutation keeps
// the lock until its bounded response settles, even if its UI is invalidated.
export function withBrowserSessionLock<T>(
  operation: () => Promise<T>,
): Promise<T> {
  return navigator.locks.request("browsemate.browser.session", operation);
}

export interface ExtensionReply {
  ok: boolean;
  code?: "AUTH_REQUIRED";
  receiverChallenge?: string;
  exchangeCode?: string;
  accountId?: string;
  alreadyAuthenticated?: boolean;
  freshAuthenticationRequired?: boolean;
}
export function extensionRequest(
  type:
    | "BROWSEMATE_BROWSER_PREPARE_V1"
    | "BROWSEMATE_BROWSER_EXCHANGE_V1"
    | "BROWSEMATE_BROWSER_GRANT_V1"
    | "BROWSEMATE_BROWSER_CANCEL_LOGIN_V1"
    | "BROWSEMATE_BROWSER_RECHECK_V1",
  payload: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<ExtensionReply | undefined> {
  const requestId = crypto.randomUUID();
  return new Promise((resolve) => {
    let extended = false;
    const finish = (value?: ExtensionReply) => {
      clearTimeout(timer);
      window.removeEventListener("message", listener);
      signal?.removeEventListener("abort", abort);
      resolve(value);
    };
    const abort = () => finish();
    const listener = (event: MessageEvent) => {
      const data = event.data as
        | (ExtensionReply & {
            source?: string;
            requestId?: string;
            pending?: boolean;
          })
        | null;
      if (
        event.source !== window ||
        event.origin !== location.origin ||
        !data ||
        data.source !== "browsemate-extension" ||
        data.requestId !== requestId
      )
        return;
      if (
        data.pending === true &&
        (type === "BROWSEMATE_BROWSER_PREPARE_V1" ||
          type === "BROWSEMATE_BROWSER_CANCEL_LOGIN_V1")
      ) {
        if (!extended) {
          extended = true;
          clearTimeout(timer);
          timer = setTimeout(
            () =>
              finish(
                type === "BROWSEMATE_BROWSER_CANCEL_LOGIN_V1"
                  ? { ok: false }
                  : undefined,
              ),
            35000,
          );
        }
        return;
      }
      if (typeof data.ok !== "boolean") return;
      if (
        data.receiverChallenge !== undefined &&
        !proof.test(data.receiverChallenge)
      )
        return;
      if (data.exchangeCode !== undefined && !proof.test(data.exchangeCode))
        return;
      if (data.accountId !== undefined && !account.test(data.accountId)) return;
      finish(data);
    };
    let timer = setTimeout(
      () => finish(),
      type === "BROWSEMATE_BROWSER_PREPARE_V1" ||
        type === "BROWSEMATE_BROWSER_CANCEL_LOGIN_V1"
        ? 1500
        : 10000,
    );
    if (signal?.aborted) {
      finish();
      return;
    }
    window.addEventListener("message", listener);
    signal?.addEventListener("abort", abort, { once: true });
    window.postMessage(
      { source: "browsemate-account-web", requestId, type, ...payload },
      location.origin,
    );
  });
}
